import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';

import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/xml/xml';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/shell/shell';
import 'codemirror/addon/edit/closetag';
import 'codemirror/addon/edit/closebrackets';
import { Box, Container } from '@mui/material';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import './Editor.styles.scss';

const Editor = (
  { mode = 'txt', code, onSubmit, readOnly = false, navigation = true },
  ref
) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();
  const [data, setData] = useState(code);
  const [isValid, setIsValid] = useState(true);

  const formatModeName = (mode) => {
    if (mode === 'sh') return 'shell';
    if (mode === 'js') return 'javascript';
    if (mode === 'json') return 'application/json';
    return mode;
  };
  const formatCode = (code) => {
    if (mode === 'json') return JSON.stringify(code, null, 4);
    return code;
  };

  const parseXML = (code) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(code, 'application/xml');
    const hasError = xmlDoc.getElementsByTagName('parsererror').length > 0;
    return !hasError;
  };

  const isValidJson = (code) => {
    try {
      JSON.parse(code);
    } catch {
      return false;
    }
    return true;
  };

  const handleChange = (value) => {
    if (mode === 'xml') !parseXML(value) ? setIsValid(false) : setIsValid(true);
    if (mode === 'json')
      !isValidJson(value) ? setIsValid(false) : setIsValid(true);

    setData(value);
  };

  const goBack = () => {
    navigate(location.state.previousLocation, { state: { ...location.state } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(mode, data);
  };

  // ================== Scroll to Bottom [START] ==================
  const editorRef = useRef(null); // Reference to the CodeMirror editor

  // Function to scroll to the bottom
  const scrollToBottom = () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const scrollInfo = editor.getScrollInfo(); // Get scroll details
      editor.scrollTo(scrollInfo.left, scrollInfo.height); // Scroll to bottom
    }
  };

  // Expose the scrollToBottom function to the parent component
  useImperativeHandle(ref, () => {
    return {
      scrollToBottom,
    };
  }, []);
  // ================== Scroll to Bottom [END] ==================

  // Tricky way of preventing default back navigation to add a state on to react router location
  // And then when the user clicks back, we navigate to the previous location in the file browser
  useEffect(() => {
    const handlePopState = () => {
      window.history.back(); // Remove the useless entry we added in the pushState
      navigate(location.state.previousLocation, {
        state: { ...location.state },
      });
    };

    // Add a new history state to prevent default back navigation.
    // This is the only way to trigger the popstate event when the user clicks back
    window.history.pushState(null, document.title, window.location.href);

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <Container maxWidth="lg">
      <form onSubmit={handleSubmit} aria-label={`${mode} editor`}>
        <CodeMirror
          value={formatCode(code)}
          options={{
            mode: formatModeName(mode),
            theme: 'material',
            lineNumbers: true,
            lineWrapping: true,
            smartIndent: true,
            matchBrackets: true,
            autoCloseTags: true,
            autoCloseBrackets: true,
            viewportMargin: Infinity,
            singleCursorHeightPerLine: false,
            readOnly: readOnly,
          }}
          onChange={(editor, data, value) => {
            handleChange(value);
          }}
          editorDidMount={(editor) => {
            editorRef.current = editor; // Store the editor instance in the ref
          }}
        />
        {navigation && (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={goBack}
              style={{ marginRight: 10 }}
              aria-label="go back"
            >
              {t('back', { ns: 'buttons' })}
            </Button>
            {!readOnly && (
              <Button
                type="submit"
                style={{ marginLeft: 10 }}
                aria-label="confirm"
                disabled={!isValid}
              >
                {t('save', { ns: 'buttons' })}
              </Button>
            )}
          </Box>
        )}
      </form>
    </Container>
  );
};

Editor.propTypes = {
  mode: PropTypes.string,
  code: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  navigation: PropTypes.bool,
};

export default forwardRef(Editor);
